import {
  CheckboxField,
  DatePickerField,
  InputField,
  SelectField,
} from "@/features/FormCard/Fields";
import { dateSchema, parseToDateValue } from "@/features/FormCard/utils";
import { Control } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  allocationTypeOptions,
  rebalancingTypeOptions,
  statusOptions,
} from "./filter";

const nullableNumber = z.preprocess((val: unknown) => {
  if (val === "" || val === null || val === undefined) return null;
  const n = Number(val);
  return Number.isNaN(n) ? null : n;
}, z.number().nullable().optional());

const nullableInteger = z.preprocess((val: unknown) => {
  if (val === "" || val === null || val === undefined) return null;
  const n = Number(val);
  return Number.isNaN(n) ? null : n;
}, z.number().int().nullable().optional());

const booleanFromUnknown = (val: unknown): boolean | undefined => {
  if (val === null || val === undefined || val === "") return undefined;
  if (typeof val === "boolean") return val;
  if (typeof val === "number") {
    if (val === 1) return true;
    if (val === 0) return false;
    return undefined;
  }
  if (typeof val === "string") {
    const v = val.trim().toLowerCase();
    if (["true", "1", "yes", "y"].includes(v)) return true;
    if (["false", "0", "no", "n"].includes(v)) return false;
  }
  return undefined;
};

const nullableBoolean = z.preprocess((val: unknown) => {
  const parsed = booleanFromUnknown(val);
  return parsed === undefined ? null : parsed;
}, z.boolean().nullable().optional());

const nullableDate = z
  .preprocess((val) => parseToDateValue(val as string), dateSchema)
  .optional();

export const versionFormSchema = z.object({
  allocation_type: nullableInteger,
  auto_start_date: nullableBoolean,
  cap: nullableNumber,
  created_at: z.string().nullable().optional(),
  floor: nullableNumber,
  live_date: nullableDate,
  live_strategies_only: nullableBoolean,
  management_fees: nullableNumber,
  min_return_type: nullableInteger,
  min_return_value: nullableNumber,
  nb_returns: nullableInteger,
  rebalancing_type: nullableInteger,
  start_date: nullableDate,
  status: nullableInteger,
  strategy_id: nullableInteger,
  transaction_costs: nullableNumber,
  updated_at: z.string().nullable().optional(),
});

export type VersionFormValues = z.infer<typeof versionFormSchema>;

export const normalizeVersionPayload = (data: Record<string, unknown>) => ({
  ...data,
  live_strategies_only:
    data.live_strategies_only === null ||
    data.live_strategies_only === undefined
      ? false
      : Boolean(data.live_strategies_only),
});

const toLabel = (raw: string) =>
  raw
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const tr = (
  t: (key: string, opts?: any) => string,
  tableName: string,
  key: string,
) =>
  t(`${tableName}.columns.${key}`, {
    ns: "tables",
    defaultValue: toLabel(key),
  });

export function VersionsFormFields({
  control,
  table_name,
}: {
  control: Control<VersionFormValues>;
  table_name: string;
}) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InputField
          name="strategy_id"
          label={tr(t, table_name, "strategy_id")}
          type="number"
          control={control}
        />
        <SelectField
          name="status"
          label={tr(t, table_name, "status")}
          options={statusOptions}
          allow_empty_option
          control={control}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <DatePickerField
          name="start_date"
          label={tr(t, table_name, "start_date")}
          control={control}
        />
        <DatePickerField
          name="live_date"
          label={tr(t, table_name, "live_date")}
          control={control}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SelectField
          name="allocation_type"
          label={tr(t, table_name, "allocation_type")}
          options={allocationTypeOptions}
          allow_empty_option
          control={control}
        />
        <SelectField
          name="rebalancing_type"
          label={tr(t, table_name, "rebalancing_type")}
          options={rebalancingTypeOptions}
          allow_empty_option
          control={control}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InputField
          name="management_fees"
          label={tr(t, table_name, "management_fees")}
          type="number"
          control={control}
        />
        <InputField
          name="transaction_costs"
          label={tr(t, table_name, "transaction_costs")}
          type="number"
          control={control}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InputField
          name="min_return_value"
          label={tr(t, table_name, "min_return_value")}
          type="number"
          control={control}
        />
        <InputField
          name="min_return_type"
          label={tr(t, table_name, "min_return_type")}
          type="number"
          control={control}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InputField
          name="cap"
          label={tr(t, table_name, "cap")}
          type="number"
          control={control}
        />
        <InputField
          name="floor"
          label={tr(t, table_name, "floor")}
          type="number"
          control={control}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InputField
          name="nb_returns"
          label={tr(t, table_name, "nb_returns")}
          type="number"
          control={control}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <CheckboxField
          name="live_strategies_only"
          label={tr(t, table_name, "live_strategies_only")}
          control={control}
        />
        <CheckboxField
          name="auto_start_date"
          label={tr(t, table_name, "auto_start_date")}
          control={control}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InputField
          name="created_at"
          label={tr(t, table_name, "created_at")}
          type="text"
          control={control}
          fieldOptions={{ isDisabled: true }}
        />
        <InputField
          name="updated_at"
          label={tr(t, table_name, "updated_at")}
          type="text"
          control={control}
          fieldOptions={{ isDisabled: true }}
        />
      </div>
    </div>
  );
}
